import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import { getPaymentQrImageUrl } from '../config';
import { Check, X, AlertTriangle, Package, RefreshCw } from 'lucide-react';

const MySubscription = () => {
  useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(1); // 1 mois par défaut
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [paymentQrImage, setPaymentQrImage] = useState('');
  const [uploadingProof, setUploadingProof] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Modal/Toast state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, plan: null });
  const [cancelModal, setCancelModal] = useState({ show: false, request: null });
  
  // Filtres et pagination pour l'historique
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const historyPerPage = 5;

  useEffect(() => {
    loadData();
  }, []);

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes, instructionsRes, periodsRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/my'),
        api.get('/services/payment-instructions'),
        api.get('/subscriptions/periods')
      ]);
      
      // Trier les packs par display_order - gérer les deux formats possibles
      const plansData = Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data?.plans || []);
      const sortedPlans = plansData.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setPlans(sortedPlans);
      setSubscription(subRes.data.current);
      setHistory(subRes.data.history || []);
      setPaymentInstructions(instructionsRes.data?.payment_instructions || '');
      if (instructionsRes.data?.payment_qr_image) {
        setPaymentQrImage(instructionsRes.data.payment_qr_image);
      }
      
      // Périodes depuis l'API - gérer les deux formats possibles
      const periodsData = Array.isArray(periodsRes.data) ? periodsRes.data : (periodsRes.data?.periods || []);
      setPeriods(periodsData);
      
      // Sélectionner la première période par défaut
      if (periodsData.length > 0) {
        setSelectedPeriod(periodsData[0].months);
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeClick = (plan) => {
    // Toujours afficher le modal de confirmation avec le guide de paiement
    setConfirmModal({ show: true, plan });
  };

  const processSubscription = async (plan) => {
    setConfirmModal({ show: false, plan: null });
    setSubscribing(true);
    
    try {
      await api.post('/subscriptions/subscribe', {
        plan_id: plan.id,
        duration_months: selectedPeriod
      });
      
      await loadData();
      showToast('Demande d\'abonnement créée ! Veuillez effectuer le paiement et uploader la preuve.', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || err.message, 'error');
    } finally {
      setSubscribing(false);
    }
  };

  const handleUploadProof = async (subscriptionId, file) => {
    if (!file) return;
    
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('proof', file);
      
      await api.post(`/subscriptions/${subscriptionId}/proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      showToast('Preuve de paiement uploadée ! En attente de validation.', 'success');
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || err.message, 'error');
    } finally {
      setUploadingProof(false);
    }
  };

  // Annuler sa demande d'abonnement
  const handleCancelRequest = async () => {
    if (!cancelModal.request) return;
    
    setCancelling(true);
    try {
      await api.delete(`/subscriptions/${cancelModal.request.id}/cancel`);
      showToast('Demande d\'abonnement annulée avec succès', 'success');
      setCancelModal({ show: false, request: null });
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || err.message, 'error');
    } finally {
      setCancelling(false);
    }
  };

  // Calculer le prix pour une période donnée
  const calculatePrice = (plan, months) => {
    const period = periods.find(p => p.months === months);
    const basePrice = plan.prix_mensuel * months;
    const discount = period?.discount || 0;
    return Math.round(basePrice * (1 - discount / 100));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  // Demande en attente
  const pendingRequest = history.find(h => h.status === 'pending');
  const currentPeriod = periods.find(p => p.months === selectedPeriod) || { discount: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4 md:p-8">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 max-w-md animate-slide-in`}>
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg ${
            toast.type === 'success' ? 'bg-green-500 text-white' :
            toast.type === 'error' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <span className="text-xl flex items-center justify-center">
              {toast.type === 'success' ? <Check className="w-5 h-5" /> : toast.type === 'error' ? <X className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            </span>
            <p className="flex-1">{toast.message}</p>
            <button 
              onClick={() => setToast({ ...toast, show: false })}
              className="text-white/80 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 animate-scale-in my-auto" style={{ marginTop: 'auto', marginBottom: 'auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="text-center">
              <span className="text-5xl flex justify-center">
                {subscription?.status === 'active' && subscription?.plan_code === confirmModal.plan?.code 
                  ? <RefreshCw className="w-12 h-12 text-blue-500" /> 
                  : subscription?.status === 'active' 
                    ? <AlertTriangle className="w-12 h-12 text-yellow-500" /> 
                    : <Package className="w-12 h-12 text-indigo-500" />}
              </span>
              <h3 className="text-xl font-bold text-gray-900 mt-4">
                {subscription?.status === 'active' && subscription?.plan_code === confirmModal.plan?.code 
                  ? 'Reconduire votre abonnement' 
                  : subscription?.status === 'active'
                    ? 'Confirmer le changement'
                    : 'Confirmer votre abonnement'}
              </h3>
              <p className="text-gray-600 mt-2">
                {subscription?.status === 'active' && subscription?.plan_code === confirmModal.plan?.code 
                  ? <>Voulez-vous reconduire votre abonnement <strong>{confirmModal.plan?.nom}</strong> pour <strong>{selectedPeriod} mois</strong> supplémentaires ?</>
                  : subscription?.status === 'active'
                    ? <>Vous avez déjà un abonnement actif. Voulez-vous souscrire au pack <strong>{confirmModal.plan?.nom}</strong> pour <strong>{selectedPeriod} mois</strong> ?</>
                    : <>Vous êtes sur le point de souscrire au pack <strong>{confirmModal.plan?.nom}</strong> pour <strong>{selectedPeriod} mois</strong>.</>
                }
              </p>
              <p className="text-lg font-semibold text-purple-600 mt-3">
                Montant à payer : {calculatePrice(confirmModal.plan, selectedPeriod).toLocaleString()} XOF
              </p>
            </div>
            
            {/* Guide de paiement */}
            {(paymentInstructions || paymentQrImage) && (
              <div className="mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                  💳 Instructions de paiement
                </h4>
                {paymentInstructions && (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                    {paymentInstructions}
                  </pre>
                )}
                {paymentQrImage && (
                  <div className={paymentInstructions ? 'mt-3 pt-3 border-t border-indigo-200' : ''}>
                    <p className="text-indigo-800 text-sm font-medium mb-2">📱 QR Code de paiement :</p>
                    <img
                      src={getPaymentQrImageUrl(paymentQrImage)}
                      alt="QR code de paiement"
                      className="max-w-[200px] max-h-[200px] rounded-lg border border-indigo-200 object-contain mx-auto"
                    />
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirmModal({ show: false, plan: null })}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => processSubscription(confirmModal.plan)}
                className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors"
              >
                ✓ Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            📦 Nos Packs d'Abonnement
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choisissez le pack adapté à vos besoins et développez votre activité sur DJAMANN
          </p>
        </div>

        {/* Sélecteur de période */}
        {periods.length > 0 && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-2xl p-2 shadow-lg">
              {periods.map(period => (
                <button
                  key={period.months}
                  onClick={() => setSelectedPeriod(period.months)}
                  className={`relative px-4 md:px-6 py-3 rounded-xl font-medium transition-all ${
                    selectedPeriod === period.months
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {period.label}
                  {period.discount > 0 && (
                    <span className={`absolute -top-2 -right-2 px-1.5 py-0.5 text-xs rounded-full font-bold ${
                      selectedPeriod === period.months
                        ? 'bg-green-400 text-green-900'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      -{period.discount}%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Abonnement actuel */}
        {subscription && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            {/* Header avec couleur du pack */}
            <div 
              className="p-6 text-white relative overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${subscription.plan_couleur}, ${subscription.plan_couleur}dd)` }}
            >
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <span className="text-4xl">{subscription.plan_icone}</span>
                  </div>
                  <div>
                    <div className="text-white/80 text-sm font-medium">Votre abonnement actuel</div>
                    <div className="text-2xl font-bold">{subscription.plan_nom}</div>
                    {subscription.plan_description && (
                      <div className="text-white/70 text-sm mt-1">{subscription.plan_description}</div>
                    )}
                  </div>
                </div>
                <div className="text-right bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                  <div className="text-white/80 text-sm">Expire le</div>
                  <div className="text-lg font-semibold">
                    {new Date(subscription.end_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div className={`text-sm font-medium ${subscription.days_remaining <= 7 ? 'text-red-200' : 'text-green-200'}`}>
                    {subscription.days_remaining > 0 ? `${subscription.days_remaining} jours restants` : '⚠️ Expire aujourd\'hui !'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modules actifs - Dynamique */}
            <div className="p-6">
              <div className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <span>✨</span>
                <span>{subscription.module_names?.length || 0} module{(subscription.module_names?.length || 0) > 1 ? 's' : ''} inclus dans votre pack</span>
              </div>
              
              {subscription.module_names && subscription.module_names.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {subscription.module_names.map((moduleName, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-100 hover:border-purple-200 hover:bg-purple-50/50 transition-all"
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: subscription.plan_couleur + '20' }}
                      >
                        <span className="text-xl">{subscription.module_icons?.[index] || '📦'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">{moduleName}</div>
                        {subscription.module_descriptions?.[index] && (
                          <div className="text-xs text-gray-500 truncate">{subscription.module_descriptions[index]}</div>
                        )}
                      </div>
                      <span className="text-green-500 text-lg">✓</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  Aucun module actif dans ce pack
                </div>
              )}
              
              {/* Barre de progression */}
              {subscription.days_remaining !== undefined && (
                <div className="mt-6 pt-6 border-t">
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Progression de l'abonnement</span>
                    <span>{Math.max(0, subscription.days_remaining)} jours restants</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        subscription.days_remaining <= 7 ? 'bg-red-500' : 
                        subscription.days_remaining <= 30 ? 'bg-orange-500' : 'bg-green-500'
                      }`}
                      style={{ 
                        width: `${Math.min(100, Math.max(0, (subscription.days_remaining / subscription.duration_months / 30) * 100))}%` 
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Overlay Demande en attente - sous la navbar */}
        {pendingRequest && (
          <div className="fixed top-16 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in my-auto max-h-[calc(100vh-6rem)] overflow-y-auto">
              {/* Header avec animation */}
              <div className="bg-gradient-to-r from-orange-400 to-amber-500 p-6 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4">
                  <span className="text-5xl animate-pulse">⏳</span>
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Demande d'abonnement en attente
                </h2>
              </div>
              
              {/* Contenu */}
              <div className="p-6">
                {/* Info pack en petit */}
                <div className="text-center mb-4">
                  <p className="text-xs text-gray-500">
                    Pack demandé : <span className="font-medium text-gray-700">{pendingRequest.plan_nom}</span> • <span className="font-medium text-orange-600">{Number(pendingRequest.amount_paid).toLocaleString()} XOF</span>
                  </p>
                </div>
                
                {!pendingRequest.payment_proof ? (
                  <div className="mb-6">
                    {/* Instructions de paiement + QR */}
                    {(paymentInstructions || paymentQrImage) && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
                        <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                          💳 Instructions de paiement
                        </h4>
                        {paymentInstructions && (
                          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                            {paymentInstructions}
                          </pre>
                        )}
                        {paymentQrImage && (
                          <div className={paymentInstructions ? 'mt-3 pt-3 border-t border-indigo-200' : ''}>
                            <p className="text-indigo-800 text-sm font-medium mb-2">📱 QR Code de paiement :</p>
                            <img
                              src={getPaymentQrImageUrl(paymentQrImage)}
                              alt="QR code de paiement"
                              className="max-w-[200px] max-h-[200px] rounded-lg border border-indigo-200 object-contain mx-auto"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Message principal bien visible */}
                    <div className="bg-orange-100 border-2 border-orange-300 rounded-2xl p-6 text-center mb-4">
                      <div className="text-5xl mb-3">📤</div>
                      <h3 className="text-lg font-bold text-orange-800 mb-2">
                        Envoyez votre preuve de paiement
                      </h3>
                      <p className="text-orange-700 text-sm">
                        Nous attendons la capture d'écran de votre paiement
                      </p>
                    </div>
                    
                    {/* Bouton upload bien visible */}
                    <label className="block cursor-pointer">
                      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl p-4 text-center hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg">
                        <span className="text-lg font-bold">📎 Cliquez pour uploader</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadProof(pendingRequest.id, e.target.files[0])}
                        disabled={uploadingProof}
                        className="hidden"
                      />
                    </label>
                    
                    {uploadingProof && (
                      <div className="flex items-center justify-center gap-2 mt-4 text-orange-600">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-600 border-t-transparent"></div>
                        <span className="font-medium">Envoi en cours...</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-center">
                    <span className="text-3xl">✅</span>
                    <p className="text-green-700 font-medium mt-2">Preuve de paiement envoyée</p>
                    <p className="text-green-600 text-sm mt-1">En attente de validation par l'admin</p>
                  </div>
                )}
                
                {/* Bouton annuler en petit */}
                <button
                  onClick={() => setCancelModal({ show: true, request: pendingRequest })}
                  className="w-full px-3 py-2 text-gray-500 text-sm hover:text-red-600 transition-all flex items-center justify-center gap-1"
                >
                  <span>✕</span>
                  <span>Annuler ma demande</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmation d'annulation */}
        {cancelModal.show && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-scale-in">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-rose-500 p-5 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-3">
                  <span className="text-4xl">⚠️</span>
                </div>
                <h3 className="text-xl font-bold text-white">
                  Confirmer l'annulation
                </h3>
              </div>
              
              {/* Contenu */}
              <div className="p-6">
                <p className="text-gray-700 text-center mb-6">
                  Êtes-vous sûr de vouloir annuler votre demande d'abonnement au pack <strong className="text-orange-600">{cancelModal.request?.plan_nom}</strong> ?
                </p>
                
                <p className="text-sm text-gray-500 text-center bg-gray-50 rounded-lg p-3 mb-6">
                  💡 Cette action est irréversible. Vous pourrez faire une nouvelle demande ultérieurement.
                </p>
                
                {/* Boutons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setCancelModal({ show: false, request: null })}
                    disabled={cancelling}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Non, garder
                  </button>
                  <button
                    onClick={handleCancelRequest}
                    disabled={cancelling}
                    className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {cancelling ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Annulation...</span>
                      </>
                    ) : (
                      <>
                        <span>✓</span>
                        <span>Oui, annuler</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Liste des packs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const isCurrentPlan = subscription?.plan_code === plan.code;
            
            return (
              <div 
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all hover:scale-105 ${
                  plan.is_popular ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                {plan.is_popular && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white px-4 py-1 text-sm font-medium rounded-bl-xl">
                    ⭐ Recommandé
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 bg-green-500 text-white px-4 py-1 text-sm font-medium rounded-br-xl">
                    ✓ Actuel
                  </div>
                )}
                
                <div className="p-6">
                  <div className="text-center mb-6">
                    <span className="text-5xl">{plan.icone}</span>
                    <h3 className="text-2xl font-bold text-gray-900 mt-3">{plan.nom}</h3>
                    <p className="text-gray-500 text-sm mt-2">{plan.description}</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    {currentPeriod.discount > 0 && (
                      <div className="text-gray-400 text-sm line-through mb-1">
                        {(plan.prix_mensuel * selectedPeriod).toLocaleString()} XOF
                      </div>
                    )}
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold" style={{ color: plan.couleur }}>
                        {calculatePrice(plan, selectedPeriod).toLocaleString()}
                      </span>
                      <span className="text-gray-500">XOF</span>
                    </div>
                    <div className="text-gray-400 text-sm">
                      pour {selectedPeriod} mois
                      {currentPeriod.discount > 0 && (
                        <span className="ml-2 text-green-600 font-medium">(-{currentPeriod.discount}%)</span>
                      )}
                    </div>
                    <div className="text-gray-300 text-xs mt-1">
                      soit {Math.round(calculatePrice(plan, selectedPeriod) / selectedPeriod).toLocaleString()} XOF/mois
                    </div>
                  </div>
                  
                  <div className="border-t pt-6 mb-6">
                    <ul className="space-y-4">
                      {plan.module_names?.map((moduleName, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">✓</span>
                          <div>
                            <div className="font-medium text-gray-800">{moduleName}</div>
                            {plan.module_descriptions?.[idx] && (
                              <div className="text-sm text-gray-500 mt-0.5">{plan.module_descriptions[idx]}</div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <button
                    onClick={() => handleSubscribeClick(plan)}
                    disabled={subscribing || (pendingRequest && !isCurrentPlan)}
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${
                      isCurrentPlan
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 hover:shadow-lg shadow-md'
                        : pendingRequest
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'text-white hover:opacity-90 hover:shadow-lg'
                    }`}
                    style={{ backgroundColor: isCurrentPlan || pendingRequest ? undefined : plan.couleur }}
                  >
                    {subscribing ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin">⏳</span> Traitement...
                      </span>
                    ) : isCurrentPlan ? (
                      <span className="flex items-center justify-center gap-2">
                        🔄 Reconduire mon abonnement
                      </span>
                    ) : pendingRequest ? 'Demande en cours...' : `Choisir pour ${selectedPeriod} mois`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Historique */}
        {history.length > 0 && (
          <div className="mt-12 bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Historique des abonnements</h3>
            
            {/* Filtres */}
            <div className="flex flex-wrap gap-3 mb-4">
              {/* Recherche */}
              <div className="flex-1 min-w-48">
                <input
                  type="text"
                  placeholder="🔍 Rechercher..."
                  value={historySearch}
                  onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Filtre par statut */}
              <select
                value={historyStatusFilter}
                onChange={(e) => { setHistoryStatusFilter(e.target.value); setHistoryPage(1); }}
                className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">✅ Actif</option>
                <option value="pending">⏳ En attente</option>
                <option value="expired">📅 Expiré</option>
                <option value="cancelled">🚫 Annulé</option>
              </select>
            </div>
            
            {/* Liste filtrée */}
            {(() => {
              const filteredHistory = history.filter(h => {
                const matchesSearch = !historySearch || 
                  h.plan_nom?.toLowerCase().includes(historySearch.toLowerCase());
                const matchesStatus = historyStatusFilter === 'all' || h.status === historyStatusFilter;
                return matchesSearch && matchesStatus;
              });
              
              const totalHistoryPages = Math.ceil(filteredHistory.length / historyPerPage);
              const paginatedHistory = filteredHistory.slice(
                (historyPage - 1) * historyPerPage,
                historyPage * historyPerPage
              );
              
              return (
                <>
                  {/* Résumé */}
                  <div className="text-sm text-gray-500 mb-3">
                    {filteredHistory.length} abonnement{filteredHistory.length > 1 ? 's' : ''} trouvé{filteredHistory.length > 1 ? 's' : ''}
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Pack</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Période</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Montant</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {paginatedHistory.map(h => (
                          <tr key={h.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <span 
                                className="px-2 py-1 text-xs rounded-full text-white"
                                style={{ backgroundColor: h.plan_couleur }}
                              >
                                {h.plan_nom}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {h.start_date && h.end_date ? (
                                <>
                                  {new Date(h.start_date).toLocaleDateString('fr-FR')} - {new Date(h.end_date).toLocaleDateString('fr-FR')}
                                </>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {Number(h.amount_paid).toLocaleString()} XOF
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                h.status === 'active' ? 'bg-green-100 text-green-700' :
                                h.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                h.status === 'expired' ? 'bg-gray-100 text-gray-600' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {h.status === 'active' ? 'Actif' :
                                 h.status === 'pending' ? 'En attente' :
                                 h.status === 'expired' ? 'Expiré' :
                                 h.status === 'cancelled' ? 'Annulé' : h.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {paginatedHistory.length === 0 && (
                          <tr>
                            <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                              Aucun abonnement ne correspond aux filtres
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalHistoryPages > 1 && (
                    <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-between gap-3">
                      <div className="text-sm text-gray-500">
                        Page {historyPage} / {totalHistoryPages}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                          disabled={historyPage === 1}
                          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          ← Précédent
                        </button>
                        
                        {/* Numéros de page */}
                        {Array.from({ length: Math.min(3, totalHistoryPages) }, (_, i) => {
                          let pageNum;
                          if (totalHistoryPages <= 3) {
                            pageNum = i + 1;
                          } else if (historyPage <= 2) {
                            pageNum = i + 1;
                          } else if (historyPage >= totalHistoryPages - 1) {
                            pageNum = totalHistoryPages - 2 + i;
                          } else {
                            pageNum = historyPage - 1 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setHistoryPage(pageNum)}
                              className={`px-3 py-1 rounded border text-sm ${
                                historyPage === pageNum 
                                  ? 'bg-purple-600 text-white border-purple-600' 
                                  : 'bg-white hover:bg-gray-100'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => setHistoryPage(p => Math.min(totalHistoryPages, p + 1))}
                          disabled={historyPage === totalHistoryPages}
                          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Suivant →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Info accès */}
        {!subscription && !pendingRequest && (
          <div className="mt-8 text-center">
            <div className="inline-block bg-blue-50 border border-blue-200 rounded-xl p-6">
              <p className="text-blue-800">
                💡 <strong>Astuce :</strong> Souscrivez à un pack pour débloquer les fonctionnalités de DJAMANN !
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscription;
